import React, { useState } from "react";
import { customerRepo, fieldJobRepo, staffRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, DataTable, Input, Modal, PageHead, Select, formatDate, useAsync, useTableState } from "../../components/ui";

const STATUS = ["pending", "assigned", "in_progress", "done"];
const JOB_TYPES = ["ติดตั้ง", "ซ่อมบำรุง", "ตรวจเช็กประจำปี", "ส่งมอบสินค้า", "รับคืนสินค้า"];
const pad = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const EMPTY = { id: "", customerId: "", address: "", type: JOB_TYPES[0], assignedStaffId: "", date: "", time: "09:00", note: "" };

export default function FieldService() {
  const { orgId, branchId, branches, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [status, setStatus] = useState("");

  const { data, loading, reload } = useAsync(
    () => fieldJobRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, filter: (row) => !status || row.status === status }),
    [orgId, status, tableState.page, tableState.perPage, tableState.search]
  );
  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);
  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openForm = (row) => {
    if (!row) return setForm({ ...EMPTY, date: localDate(new Date()) });
    const when = new Date(row.scheduledAt);
    const valid = !Number.isNaN(when.getTime());
    setForm({ ...EMPTY, ...row, assignedStaffId: row.assignedStaffId || "", date: valid ? localDate(when) : "", time: valid ? localTime(when) : "09:00", note: row.note || "" });
  };

  const pickCustomer = (customerId) => {
    const customer = (customers || []).find((c) => c.id === customerId);
    setForm({ ...form, customerId, address: customer?.address || form.address });
  };

  const saveForm = async () => {
    const customer = (customers || []).find((c) => c.id === form.customerId);
    if (!customer) return toast(tx("เลือกลูกค้า", "Select a customer"), "error");
    if (!form.type.trim()) return toast(tx("ระบุประเภทงาน", "Job type required"), "error");
    if (!form.address.trim()) return toast(tx("กรอกที่อยู่หน้างาน", "Site address required"), "error");
    const when = new Date(`${form.date}T${form.time || "00:00"}`);
    if (!form.date || Number.isNaN(when.getTime())) return toast(tx("เลือกวันและเวลานัด", "Pick the appointment date and time"), "error");
    setSaving(true);
    try {
      const payload = {
        customerId: customer.id,
        customerName: customer.name,
        address: form.address.trim(),
        type: form.type.trim(),
        assignedStaffId: form.assignedStaffId || null,
        scheduledAt: when.toISOString(),
        note: form.note.trim(),
      };
      if (form.id) {
        // มอบหมายช่างครั้งแรกในใบที่ยัง pending -> assigned อัตโนมัติ
        const current = data?.rows?.find((r) => r.id === form.id);
        const status = current?.status === "pending" && payload.assignedStaffId ? "assigned" : current?.status;
        await fieldJobRepo.update(orgId, form.id, { ...payload, ...(status ? { status } : {}) });
      } else {
        const rows = await fieldJobRepo.all(orgId);
        const max = rows.reduce((m, row) => Math.max(m, Number(String(row.jobNo || "").replace(/\D/g, "")) || 0), 9000);
        await fieldJobRepo.create(orgId, {
          ...payload,
          jobNo: `JB-${max + 1}`,
          status: payload.assignedStaffId ? "assigned" : "pending",
          branchId: customer.branchId || branchId || branches?.[0]?.id || "",
          createdAt: new Date().toISOString(),
        });
      }
      setForm(null);
      reload();
      toast(tx("บันทึกใบสั่งงานแล้ว", "Job saved"), "success");
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const staffName = (id) => staff?.find((row) => row.id === id)?.name || "-";

  const update = async (row, patch) => {
    await fieldJobRepo.update(orgId, row.id, patch);
    reload();
    toast(tx("อัปเดตใบสั่งงานแล้ว", "Job updated"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("ใบสั่งงานช่าง / บริการนอกสถานที่", "Field service jobs")}
        subtitle={tx("มอบหมายงานและติดตามสถานะช่างนอกสถานที่", "Assign and track field technicians")}
        actions={<Button variant="primary" onClick={() => openForm(null)}>+ {tx("สร้างใบสั่งงาน", "New job")}</Button>}
      />

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหา", "Search")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
        <Select options={STATUS} value={status} onChange={(e) => setStatus(e.target.value)} placeholder={tx("ทุกสถานะ", "All status")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        onRowClick={openForm}
        emptyText={tx("ยังไม่มีใบสั่งงาน", "No jobs")}
        columns={[
          { key: "jobNo", label: tx("เลขที่", "Job no.") },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "type", label: tx("ประเภทงาน", "Type") },
          { key: "assignedStaffId", label: tx("ผู้รับผิดชอบ", "Assigned to"), render: (row) => staffName(row.assignedStaffId) },
          { key: "scheduledAt", label: tx("นัดหมาย", "Scheduled"), render: (row) => formatDate(row.scheduledAt, true) },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => (
              <select className="select-crm" style={{ width: "auto", padding: "3px 8px" }} value={row.status} onClick={(e) => e.stopPropagation()} onChange={(e) => update(row, { status: e.target.value })}>
                {STATUS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ),
          },
        ]}
      />

      <Modal
        open={!!form}
        title={form?.id ? `${tx("แก้ไขใบสั่งงาน", "Edit job")} ${form.jobNo || ""}` : tx("สร้างใบสั่งงาน", "New job")}
        onClose={() => setForm(null)}
        footer={<><Button onClick={() => setForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveForm}>{tx("บันทึก", "Save")}</Button></>}
      >
        {form ? (
          <>
            <Select
              label={tx("ลูกค้า", "Customer")}
              placeholder={tx("เลือกลูกค้า", "Select customer")}
              options={(customers || []).map((c) => ({ value: c.id, label: `${c.name} · ${c.phone || c.code || ""}` }))}
              value={form.customerId}
              onChange={(e) => pickCustomer(e.target.value)}
            />
            <Input label={tx("ที่อยู่หน้างาน", "Site address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Input label={tx("ประเภทงาน", "Job type")} list="field-job-types" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
            <datalist id="field-job-types">{JOB_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
            <Select
              label={tx("มอบหมายช่าง", "Assign technician")}
              placeholder={tx("ยังไม่มอบหมาย", "Unassigned")}
              options={(staff || []).map((row) => ({ value: row.id, label: `${row.name}${row.position ? ` · ${row.position}` : ""}` }))}
              value={form.assignedStaffId}
              onChange={(e) => setForm({ ...form, assignedStaffId: e.target.value })}
            />
            <Input label={tx("วันที่นัด", "Date")} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Input label={tx("เวลานัด", "Time")} type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            <Input label={tx("หมายเหตุ", "Note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
